import React, { useEffect, useRef, useState } from "react"
import { Alert, Drawer, Select, Tabs } from "antd"
import { CheckCircleOutlined, ExclamationCircleOutlined, StopOutlined } from "@ant-design/icons"
import type { Editor as TiptapEditor } from "@tiptap/react"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { Variant } from "types/questionnaire.types"
import {
  SurveyAnswers,
  SurveyAnswerValue,
  SurveyCard,
  SurveyCellError,
  SurveyFillRequest,
  SurveyResponse,
  SurveyRoutingStep,
  SurveyTestError,
} from "types/surveyTest.types"
import { failureOf } from "utils/errMsgOf"
import { useSurveyTest } from "../api/useSurveyTest"
import { answeredTree } from "../model/answered"
import { RoutingGroup, routingGroups, routingTarget } from "../model/routing"
import {
  afterResponse,
  answersToSend,
  cardCells,
  convenienceErrors,
  formOnScreen,
  pageTitle,
  screenToCheck,
  TestState,
} from "../model/session"
import AnsweredTree from "./AnsweredTree.component"
import { Outcome, RoutingCondition } from "./RoutingCondition.component"
import ScreenView, { CARD, classes, ErrorList } from "./ScreenView.component"

export interface SurveyTestDrawerProps {
  open: boolean
  onClose: () => void
  projectId: string
  owner: string
  variants: Variant[]
  /** The questionnaire's own languages, from its #LANGUAGE line above any variant: Master's, as a variant has its own */
  masterLanguages: string[]
  /** The variant the Editor shows, "" for Master */
  currentVariant: string
  /**
   * The project's design language: every test offers it first and starts on it. It shows the master text,
   * as the Editor's Preview does.
   */
  designLanguage: string
  /** The Editor: an edit while a test runs says the test is on the questionnaire as it was saved before */
  editor?: Pick<TiptapEditor, "on" | "off"> | null
  /** Saves the questionnaire: Restart and a change of variant test it as it is now */
  onSave?: () => Promise<boolean | void>
}

/**
 * The languages a test offers, as the Editor's Preview does: the project's design language first, then
 * the languages the variant lists, or for Master ("") the questionnaire's own, each once.
 */
export const languagesFor = (
  variants: Variant[],
  variant: string,
  projectDesign: string,
  master: string[]
): string[] => {
  const own = variant ? variants.find((v) => v.name === variant)?.languages ?? [] : master
  return [projectDesign, ...own].filter((l, i, all) => l !== "" && all.indexOf(l) === i)
}

/**
 * The language a request sends for the one chosen: none ("") for the project's design language, which the
 * engine reads as the variant's design language, the first language of its #LANGUAGE list; any other by
 * name, so the Conductor merges its translations and the engine finds it in the test's #LANGUAGE list.
 */
export const languageToSend = (chosen: string, projectDesign: string): string =>
  chosen === projectDesign ? "" : chosen

/** A failed call as the drawer shows it: the server's message, and its code when it gave one. */
interface Failure {
  message: string
  code?: string
}

/** Whose answer a call waits for: a start the drawer awaits, or the session it shows. */
type Ticket = { start: object } | { session: string }

/** The message for a failure that gives none. */
const FAILED = "The test could not continue."

/** The message for a page the server started but has no card to show on. */
const NO_CARDS = "This page could not be shown."

/** The note on the screen fetched again when the session had already moved past the step answered. */
const MOVED_ON = "The test had already moved on; this is where it is now."

/** The banner once the questionnaire is edited while a test runs: the session keeps the version it started on. */
const CHANGED = "The questionnaire has changed. Restart to test it."

/** The failure of a session that is gone: expired, ended, or lost with an engine restart. */
const ENDED = "This test has ended. Start again."

/** The heading over the problems that refuse a start, listed one per line. */
const REFUSED = "This questionnaire cannot be tested yet:"

/** The drawer's width; the side panel sits beside the question when the body is wider than 760px. */
const WIDTH = "min(880px, 94vw)"

/**
 * The body is the drawer's width less antd's 24px padding on each side, and less a scrollbar of up to
 * 16px when the page is long. The drawer is 94vw up to 880px, so from a window of 880px the body is
 * 760px or more either way. Every Tailwind-styled element leads with a plain class: jsdom's selector
 * engine fails on a first class with "." or "[".
 */
const BODY = "grid gap-6 min-[880px]:grid-cols-[minmax(0,1fr)_240px]"

/** What a browser can focus inside a field that is marked invalid. */
const FOCUSABLE = "input, textarea, select, button, [tabindex]"

/** The variant a test starts on: the one the Editor shows, or else the first. */
const startVariant = (variants: Variant[], current: string): string => current || variants[0]?.name || ""

/** One question in one loop context: a card's React key. */
const cardKey = (card: SurveyCard): string => [card.question, ...card.loop.map((l) => l.iteration)].join("//")

/** An answer as the end card lists it. */
const valueText = (v: SurveyAnswerValue): string => (v === null ? "" : Array.isArray(v) ? v.join(", ") : String(v))

/** The end card's title. */
const endTitle = (response: SurveyResponse): string =>
  response.status === "terminated" ? "The survey ended here (terminated)" : "End of the survey"

/** A failure as the drawer shows it. */
const failureFrom = (e: unknown): Failure => ({
  message: failureOf(e) || FAILED,
  code: e instanceof SurveyTestError ? e.code : undefined,
})

/** The first field in `page` marked invalid, as something the focus can go to: the chosen radio of a list, say. */
const firstInvalidField = (page: HTMLElement): HTMLElement | null => {
  const invalid = page.querySelector<HTMLElement>('[aria-invalid="true"]')
  if (!invalid) return null
  if (invalid.matches(FOCUSABLE)) return invalid
  return invalid.querySelector<HTMLElement>("input:checked") ?? invalid.querySelector<HTMLElement>(FOCUSABLE)
}

/** The end of the survey, or a terminate, with every answer when the rest was filled at random. */
const EndCard: React.FC<{ response: SurveyResponse; onRestart: () => void }> = ({ response, onRestart }) => {
  const terminated = response.status === "terminated"
  const answers = response.answers ? Object.entries(response.answers) : undefined
  return (
    <div className={CARD}>
      <div className="flex items-start gap-3">
        {terminated ? (
          <StopOutlined className="text-2xl text-red-600 mt-0.5" />
        ) : (
          <CheckCircleOutlined className="text-2xl text-green-600 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-lg font-medium text-gray-900">{endTitle(response)}</div>
          {terminated && response.terminated?.text && (
            <div className="mt-1 text-gray-700 whitespace-pre-wrap break-words">{response.terminated.text}</div>
          )}
          {terminated && response.terminated?.at && (
            <div className="mt-1 text-xs text-gray-400 break-words">Question: {response.terminated.at}</div>
          )}
        </div>
      </div>
      {answers && answers.length > 0 && (
        // Long unbroken cell ids and values wrap anywhere, so the table never runs past the card.
        <table className="mt-5 w-full text-sm [overflow-wrap:anywhere]">
          <tbody>
            {answers.map(([cell, v]) => (
              <tr key={cell} className="border-t border-gray-100">
                <th scope="row" className="py-1 pr-4 text-left align-top font-normal text-gray-500">
                  {cell}
                </th>
                <td className="py-1 align-top text-gray-900">{valueText(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-5">
        <DataQButton type="secondary" onClick={onRestart}>
          Restart
        </DataQButton>
      </div>
    </div>
  )
}

/** A page the server started with no card to show: it says so, and offers Restart; Fill stays in the footer. */
const NoCardsCard: React.FC<{ onRestart: () => void }> = ({ onRestart }) => (
  <div className={CARD}>
    <div className="flex items-start gap-3">
      <ExclamationCircleOutlined className="text-2xl text-amber-500 mt-0.5" />
      <div className="min-w-0 flex-1 text-lg font-medium text-gray-900">{NO_CARDS}</div>
    </div>
    <div className="mt-5">
      <DataQButton type="secondary" onClick={onRestart}>
        Restart
      </DataQButton>
    </div>
  </div>
)

/**
 * A failed call: the server's message, the problems that refuse a start as a list, or a session that is gone.
 * Try again sends the same call again. A refused start and a test that has already ended cannot succeed that
 * way, so they offer Restart, which saves the questionnaire first.
 */
const FailureAlert: React.FC<{ failure: Failure; onTryAgain: () => void; onStartAgain: () => void }> = ({
  failure,
  onTryAgain,
  onStartAgain,
}) => {
  if (failure.code === "session_not_found")
    return (
      <Alert
        type="error"
        showIcon
        message={ENDED}
        action={
          <DataQButton size="small" onClick={onStartAgain}>
            Start again
          </DataQButton>
        }
      />
    )
  const problems = failure.code === "start_refused" ? failure.message.split("\n").map((l) => l.trim()).filter(Boolean) : []
  const restartOnly = failure.code === "start_refused" || failure.code === "session_closed"
  return (
    <Alert
      type="error"
      showIcon
      message={
        problems.length ? (
          <div className="break-words">
            <div>{REFUSED}</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        ) : (
          <span className="break-words">{failure.message}</span>
        )
      }
      action={
        restartOnly ? (
          <DataQButton size="small" onClick={onStartAgain}>
            Restart
          </DataQButton>
        ) : (
          <DataQButton size="small" onClick={onTryAgain}>
            Try again
          </DataQButton>
        )
      }
    />
  )
}

/** The routing steps the "Why" tab lists, each as a readable condition. */
const WhyList: React.FC<{ steps: SurveyRoutingStep[] }> = ({ steps }) => (
  <ul className="space-y-2">
    {steps.map((s, i) => (
      <li key={i}>
        <RoutingCondition step={s} />
      </li>
    ))}
  </ul>
)

/** Every routing decision so far, under the page it led to, each with what it came to. */
const RoutingTree: React.FC<{ groups: RoutingGroup[] }> = ({ groups }) =>
  groups.length ? (
    <ol className="space-y-3">
      {groups.map((g, i) => (
        <li key={i}>
          <div className="break-words text-sm text-gray-500">
            to <span className="font-medium text-gray-900">{g.to}</span>
          </div>
          <ul className="mt-1 ml-1 space-y-2 border-l border-gray-200 pl-3">
            {g.steps.map((s, j) => (
              <li key={j} className="flex items-start justify-between gap-2">
                <RoutingCondition step={s} />
                <Outcome step={s} />
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  ) : (
    <div className="text-sm text-gray-500">No routing decisions yet.</div>
  )

/**
 * The link test: a click-through of the saved questionnaire on a server session, one page at a time. The
 * session holds the answers and the routing; the drawer holds the page's form, sends it, and shows the
 * screen the session answers with, the questions of a page as one card each. Only the drawer's own close
 * button closes it, which ends the session: opening it again starts a new test.
 */
const SurveyTestDrawer: React.FC<SurveyTestDrawerProps> = ({
  open,
  onClose,
  projectId,
  owner,
  variants,
  masterLanguages,
  currentVariant,
  designLanguage,
  editor,
  onSave,
}) => {
  const api = useSurveyTest(projectId, owner)
  const [variant, setVariant] = useState(() => startVariant(variants, currentVariant))
  // The language chosen, "" until one is: the design language. Every request sends it as languageToSend says.
  const [language, setLanguage] = useState("")
  const [test, setTest] = useState<TestState | null>(null)
  const [form, setForm] = useState<SurveyAnswers>({})
  // The browser's checks and the server's errors, by cell; a change drops those it may answer.
  const [errors, setErrors] = useState<SurveyCellError[]>([])
  const [failure, setFailure] = useState<Failure | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // The questionnaire was edited since the test started.
  const [changed, setChanged] = useState(false)
  const [pending, setPending] = useState(false)
  // Counts the answers and the failed browser checks: each sends the focus where the tester goes on.
  const [focusTick, setFocusTick] = useState(0)
  // The form as the last change left it: Enter can arrive in the same event as that change, before a render.
  const formRef = useRef<SurveyAnswers>({})
  // The test as the last answer left it, for a click in the same event.
  const testRef = useRef<TestState | null>(null)
  // The values the page shown was rebuilt on when an answer revealed a card; none on a page shown afresh.
  const rebuiltOnRef = useRef<SurveyAnswers>({})
  // A request runs: Enter or a second click must not send the page twice.
  const pendingRef = useRef(false)
  // The session the drawer shows; an answer for any other is dropped.
  const sessionRef = useRef<string | null>(null)
  // The start the drawer awaits; a session another start opened, or one opened after the drawer closed, is ended unseen.
  const startRef = useRef<object | null>(null)
  // The call that failed, which Try again sends again.
  const retryRef = useRef<(() => void) | null>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  // The screen the focus last went to the top of.
  const focusedKey = useRef("")

  const setFormNow = (f: SurveyAnswers) => {
    formRef.current = f
    setForm(f)
  }

  const setTestNow = (t: TestState | null) => {
    testRef.current = t
    setTest(t)
  }

  const done = () => {
    pendingRef.current = false
    setPending(false)
  }

  const isCurrent = (t: Ticket): boolean =>
    "start" in t ? startRef.current === t.start : sessionRef.current === t.session

  /** Ends a session and forgets it: whatever it answers changes nothing. */
  const endSession = (session: string | null) => {
    if (session) void api.end.mutateAsync({ session }).catch(() => undefined)
  }

  /**
   * Shows a response. A new screen takes an empty form. A page the engine rebuilt (a higher revision: an
   * answer revealed or hid a card, or changed the options a cell shows) becomes the reference for the
   * check on Next: shown again without errors, its form takes the values it was rebuilt on and says what
   * a new card still needs; refused, it keeps what the tester entered that the new cards still show, and
   * puts the errors on their cells. A refused screen that was not rebuilt, or the same screen in another
   * language, keeps the form.
   */
  const show = (r: SurveyResponse, sent: SurveyAnswers) => {
    const before = testRef.current?.session === r.session ? testRef.current.response : undefined
    setTestNow(afterResponse(testRef.current, sent, r))
    const sameScreen =
      before !== undefined && before.status === "question" && r.status === "question" && r.step === before.step
    if (!sameScreen) {
      rebuiltOnRef.current = {}
      setFormNow({})
      setErrors(r.errors)
    } else if (r.screen.revision > before.screen.revision) {
      rebuiltOnRef.current = r.values
      if (r.errors.length) {
        setFormNow(formOnScreen(r.screen, formRef.current))
        setErrors(r.errors)
      } else {
        const values = formOnScreen(r.screen, r.values)
        setFormNow(values)
        setErrors(convenienceErrors(r.screen, values))
      }
    } else setErrors(r.errors)
    setFocusTick((t) => t + 1)
  }

  /**
   * Sends a call for `ticket` and shows its answer, unless the drawer has moved on by then. A step the
   * session had already passed, or a test that has already ended (another tab finished it, say), fetches
   * where it is now (once: `resync`); any other failure shows, and Try again sends the same call again: a
   * submit is applied once per step, so a repeat is safe.
   */
  const send = async (
    ticket: Ticket,
    call: () => Promise<SurveyResponse>,
    sent: SurveyAnswers,
    then?: () => void,
    resync = true
  ): Promise<void> => {
    pendingRef.current = true
    setPending(true)
    setFailure(null)
    setNotice(null)
    let r: SurveyResponse
    try {
      r = await call()
    } catch (e) {
      if (!isCurrent(ticket)) return
      const elsewhere = e instanceof SurveyTestError && (e.code === "stale_step" || e.code === "session_closed")
      if (resync && elsewhere && "session" in ticket) {
        const session = ticket.session
        return send(ticket, () => api.screen.mutateAsync({ session }), sent, () => setNotice(MOVED_ON), false)
      }
      retryRef.current = () => void send(ticket, call, sent, then, resync)
      setFailure(failureFrom(e))
      done()
      return
    }
    if (!isCurrent(ticket)) {
      if ("start" in ticket) endSession(r.session)
      return
    }
    if ("start" in ticket) {
      startRef.current = null
      sessionRef.current = r.session
    }
    show(r, sent)
    then?.()
    done()
  }

  /**
   * A new test on variant `v` in language `lang`; the session shown so far is ended. Restart and a change
   * of variant save the questionnaire first, and end the old session before the new one starts.
   */
  const startTest = async (v: string, lang: string, save: boolean) => {
    const ticket = { start: {} }
    const old = sessionRef.current
    startRef.current = ticket.start
    sessionRef.current = null
    rebuiltOnRef.current = {}
    setTestNow(null)
    setFormNow({})
    setErrors([])
    setFailure(null)
    setNotice(null)
    setChanged(false)
    focusedKey.current = ""
    pendingRef.current = true
    setPending(true)
    if (save) {
      try {
        await onSave?.()
      } catch {
        // A failed save: the test starts on the questionnaire as it was last saved.
      }
      if (old) await api.end.mutateAsync({ session: old }).catch(() => undefined)
      // The drawer closed, or another start began, while it saved.
      if (startRef.current !== ticket.start) return
    } else endSession(old)
    const body = { variant: v, language: languageToSend(lang, designLanguage) }
    await send(ticket, () => api.start.mutateAsync(body), {})
  }

  // Opening the drawer starts a new test on the variant the Editor shows; closing it ends the test.
  useEffect(() => {
    if (!open) return
    const v = startVariant(variants, currentVariant)
    setVariant(v)
    setLanguage("")
    void startTest(v, "", false)
    return () => {
      startRef.current = null
      endSession(sessionRef.current)
      sessionRef.current = null
      done()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // An edit while the drawer is open: the session keeps the version it started on, so the banner says so.
  useEffect(() => {
    if (!open || !editor) return
    const onEdit = () => setChanged(true)
    editor.on("update", onEdit)
    return () => {
      editor.off("update", onEdit)
    }
  }, [open, editor])

  const response = test?.response
  const cards = response?.status === "question" ? response.screen.cards : []
  const ended = response !== undefined && response.status !== "question"
  const noCards = response?.status === "question" && cards.length === 0

  const pageKey = !response ? "" : ended ? response.status : `${response.step}`

  // After an answer or a failed browser check: a new screen takes the focus at its top; the same screen
  // sends it to the first field with an error. Otherwise a focus lost while the page was busy comes back to it.
  useEffect(() => {
    const page = pageRef.current
    if (!focusTick || !page) return
    if (pageKey !== focusedKey.current) {
      focusedKey.current = pageKey
      page.focus()
      return
    }
    const field = firstInvalidField(page)
    if (field) field.focus()
    else if (!document.activeElement || document.activeElement === document.body) page.focus()
    // Only an answer or a check moves the focus, not a change of the page's key alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick])

  const restart = () => void startTest(variant, language, true)

  const changeVariant = (v: string) => {
    // A language the new variant offers is kept; any other falls back to the design language.
    const lang = languagesFor(variants, v, designLanguage, masterLanguages).includes(language) ? language : ""
    setVariant(v)
    setLanguage(lang)
    void startTest(v, lang, true)
  }

  /** The same screen in language `lang`; the select takes it once the server does. Without a session, a start in it. */
  const changeLanguage = (lang: string) => {
    const t = testRef.current
    if (!t) {
      setLanguage(lang)
      void startTest(variant, lang, false)
      return
    }
    const body = { session: t.session, language: languageToSend(lang, designLanguage) }
    void send({ session: t.session }, () => api.language.mutateAsync(body), {}, () => setLanguage(lang))
  }

  const onNext = () => {
    const t = testRef.current
    if (pendingRef.current || !t || t.response.status !== "question" || !t.response.screen.cards.length) return
    const values = formRef.current
    // On a page shown again, the check stops at the first card whose answer changed since the rebuild: the
    // cards after it are sent as they are, and the engine decides whether they still apply.
    const problems = convenienceErrors(screenToCheck(t.response.screen, values, rebuiltOnRef.current), values)
    setErrors(problems)
    if (problems.length) {
      setFocusTick((n) => n + 1)
      return
    }
    const answers = answersToSend(t.response.screen, values)
    const body = { session: t.session, step: t.response.step, answers }
    void send({ session: t.session }, () => api.submit.mutateAsync(body), answers)
  }

  /** The rest at random, after the screen's answers so far; the session checks them as it checks Next. */
  const onFill = () => {
    const t = testRef.current
    if (pendingRef.current || !t || t.response.status !== "question") return
    setErrors([])
    const answers = answersToSend(t.response.screen, formRef.current)
    const body: SurveyFillRequest = { session: t.session, step: t.response.step }
    if (Object.keys(answers).length) body.answers = answers
    void send({ session: t.session }, () => api.fill.mutateAsync(body), answers)
  }

  /** A change drops the errors it may answer: the cell's, and for a cell, those of its open texts. */
  const onChange = (cell: string, value: SurveyAnswerValue | undefined) => {
    const f = { ...formRef.current }
    if (value === undefined) delete f[cell]
    else f[cell] = value
    setFormNow(f)
    const own = cards.flatMap((c) => c.cells).find((c) => c.id === cell)
    const cleared = new Set([cell, ...(own ? Object.values(own.open) : [])])
    setErrors((errs) => errs.filter((e) => !cleared.has(e.cell)))
  }

  // Each card shows the errors on its own cells; the rest show once above the cards.
  const owned = cards.map((card) => new Set(cardCells(card)))
  const leftover = ended ? [] : errors.filter((e) => !owned.some((ids) => ids.has(e.cell)))

  const canNext = cards.length > 0
  const canFill = response?.status === "question"
  const screenNo = response?.step ?? 1

  const announcement = !response
    ? ""
    : ended
      ? `${endTitle(response)}.`
      : noCards
        ? `Screen ${screenNo}: ${NO_CARDS}`
        : `Screen ${screenNo}: ${pageTitle(cards)}`

  const header = (
    <div className="flex items-center gap-2">
      {variants.length > 0 && (
        <Select
          aria-label="Variant"
          className="w-36"
          value={variant}
          disabled={pending}
          options={variants.map((v) => ({ value: v.name, label: v.name }))}
          onChange={changeVariant}
        />
      )}
      <Select
        aria-label="Language"
        className="w-28"
        value={language || designLanguage}
        disabled={pending}
        options={languagesFor(variants, variant, designLanguage, masterLanguages).map((l) => ({ value: l, label: l }))}
        onChange={changeLanguage}
      />
      <DataQButton type="secondary" onClick={restart}>
        Restart
      </DataQButton>
    </div>
  )

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-500">{ended || !response ? null : `Screen ${screenNo}`}</span>
      <div className="flex items-center gap-2">
        {ended ? (
          <DataQButton type="secondary" onClick={restart}>
            Restart
          </DataQButton>
        ) : (
          <>
            {/* A lesser action: the house default button, as the Dummy data panel's "New seed". */}
            <DataQButton onClick={onFill} isDisabled={!canFill || pending}>
              Fill the rest at random
            </DataQButton>
            <DataQButton type="primary" onClick={onNext} isLoading={pending} isDisabled={!canNext}>
              Next
            </DataQButton>
          </>
        )}
      </div>
    </div>
  )

  // The engine explains the page's first card only.
  const why = response?.why ?? []
  const alwaysAsked =
    cards.length > 1
      ? "The first question on this page is always asked."
      : cards.length === 1
        ? "This question is always asked."
        : null
  // The decisions that led to each screen answered, and to where the latest answer went.
  const routing = response && test ? routingGroups(response.trace, test.history, routingTarget(response)) : []
  const sideTabs = [
    {
      key: "why",
      label: "Why",
      children:
        why.length > 0 ? (
          <WhyList steps={why} />
        ) : alwaysAsked ? (
          <div className="text-sm text-gray-500">{alwaysAsked}</div>
        ) : null,
    },
    { key: "routing", label: "Routing", children: <RoutingTree groups={routing} /> },
    { key: "answered", label: "Answered", children: <AnsweredTree nodes={answeredTree(test?.history ?? [])} /> },
  ]

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={WIDTH}
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
      title="Test survey"
      extra={header}
      footer={footer}
    >
      <div className={BODY}>
        <div className="min-w-0 space-y-4">
          <div className="text-xs text-gray-500">This tests the saved questionnaire. Save your changes to test them.</div>
          {/* Read out politely when the page changes: its number and questions, or how the survey ended. */}
          <div role="status" className="sr-only">
            {announcement}
          </div>
          {changed && <Alert type="warning" showIcon message={CHANGED} />}
          {failure !== null && (
            <FailureAlert
              failure={failure}
              onTryAgain={() => retryRef.current?.()}
              onStartAgain={restart}
            />
          )}
          {notice !== null && <Alert type="info" showIcon message={notice} />}
          {!response && pending && <Loader fontSize={40} />}
          {response && (
            <div
              ref={pageRef}
              tabIndex={-1}
              data-testid="survey-test-page"
              aria-busy={pending || undefined}
              inert={pending || undefined}
              className={classes(
                "space-y-4 outline-none transition-opacity",
                pending && "opacity-60 pointer-events-none"
              )}
            >
              {leftover.length > 0 && <Alert type="error" showIcon message={<ErrorList errors={leftover} />} />}
              {ended ? (
                <EndCard response={response} onRestart={restart} />
              ) : noCards ? (
                <NoCardsCard onRestart={restart} />
              ) : (
                cards.map((card, i) => (
                  <ScreenView
                    key={cardKey(card)}
                    card={card}
                    values={form}
                    errors={errors.filter((e) => owned[i].has(e.cell))}
                    onChange={onChange}
                    onSubmit={onNext}
                  />
                ))
              )}
            </div>
          )}
        </div>
        {response && (
          <div className="min-w-0">
            <Tabs size="small" items={sideTabs} />
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default SurveyTestDrawer
